import { useState } from 'react';
import {
  TabButtons,
  RecipientType,
  MessageGroupMember,
  formatTimeWithLocale,
  formatDateTimeShortWithLocale,
  CommunicationAvatar,
  colors,
} from 'remote/global';
import { Grid, TextField, Paper, Autocomplete, Box, Typography, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  useOperatorsUserMessagingMessagesQuery,
  useOperatorsUserMessagingUsersQuery,
} from 'api/queries/operators';
import useOperatorTimezone from '@/utils/hooks/useOperatorTimezone';
import dayjs from 'dayjs';

interface MessageElementProps {
  title: string;
  subtitle: string;
  description: string;
  date: string;
  seenText: string;
  senderName: string;
}

function MessageElement({
  senderName,
  title,
  subtitle,
  description,
  date,
  seenText,
}: MessageElementProps) {
  return (
    <Grid
      item
      container
      xs={12}
      sx={{
        padding: '16px',
      }}
    >
      <Grid item width='32px' justifyContent='center' mr='16px'>
        <CommunicationAvatar name={senderName} />
      </Grid>
      <Grid item container xs spacing={1}>
        <Grid item xs={12} display='flex' justifyContent='space-between'>
          <Box>
            <Typography
              sx={{
                color: colors.gray2,
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '20px',
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                color: colors.gray2,
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '20px',
              }}
            >
              {subtitle}
            </Typography>
          </Box>
          <Typography
            sx={{
              color: colors.gray3,
              fontSize: '12px',
            }}
          >
            {date}
          </Typography>
        </Grid>
        <Grid item xs={12} display='flex'>
          <Divider
            orientation='vertical'
            flexItem
            sx={{
              marginRight: '8px',
              '&.MuiDivider-root': {
                borderWidth: '2px',
                borderRadius: '2px',
              },
            }}
          />
          <Typography
            sx={{
              color: colors.gray2,
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
            }}
          >
            {description}
          </Typography>
        </Grid>
        <Grid item xs={12} textAlign='right'>
          <Typography
            sx={{
              color: colors.uiBlue600,
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '18px',
            }}
          >
            {seenText}
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}

interface Props {
  messagesActiveTab: number;
  setMessagesActiveTab: (value: number) => void;
}

function getRecipientType(messagesActiveTab: number): RecipientType {
  switch (messagesActiveTab) {
    case 0:
      return RecipientType.ALL;
    case 1:
      return RecipientType.GROUP;
    case 2:
      return RecipientType.USER;
    default:
      return RecipientType.ALL;
  }
}

function MessagesHistoryList({ messagesActiveTab, setMessagesActiveTab }: Props) {
  const { t, i18n } = useTranslation();
  const recipientType = getRecipientType(messagesActiveTab);
  const [selectedMember, setSelectedMember] = useState<MessageGroupMember | null>(null);
  const operatorTimezone = useOperatorTimezone();

  const { data: messageGroupUsers } = useOperatorsUserMessagingUsersQuery({
    pageNo: '0',
  });
  const { data: messages } = useOperatorsUserMessagingMessagesQuery({
    pageNo: '0',
    userId: selectedMember ? String(selectedMember.userId) : '',
    recipientType,
  });

  const getSeenText = (seenCount: number, totalCount: number) => {
    if (seenCount === 0) {
      return t('MessagesView.chatbox.sent');
    }
    if (seenCount === totalCount && totalCount === 1) {
      return t('MessagesView.chatbox.seen');
    }
    if (seenCount === totalCount) {
      return t('MessagesView.chatbox.seenByAll');
    }
    return t('MessagesView.chatbox.seenByNumber', { seenCount, totalCount });
  };

  return (
    <Box
      alignItems='baseline'
      flexDirection='column'
      display='flex'
      height={{ xs: 'calc(100vh - 290px)', lg: 'calc(100vh - 240px)' }}
      mr='24px'
      maxWidth='458px'
      width='100%'
      component={Paper}
      elevation={3}
      p='30px 25px'
      sx={{ borderRadius: '8px' }}
      mb={{
        xs: '24px',
        lg: '0px',
      }}
    >
      <Box sx={{ marginBottom: '1.5rem' }}>
        <Grid container rowSpacing={2}>
          <Typography sx={{ fontSize: '18px', fontWeight: 500, lineHeight: '26px' }}>
            {t('MessagesView.history.title')}
          </Typography>
          <Grid item xs={12}>
            <TabButtons
              activeTab={messagesActiveTab}
              setActiveTab={setMessagesActiveTab}
              tabs={Object.values(RecipientType).map((key) => t(`RecipientType.${key}`))}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              sx={{
                maxWidth: '343px',
              }}
              fullWidth
              disablePortal
              id='message-group-member'
              value={selectedMember ?? null}
              onChange={(_, newValue) => {
                setSelectedMember(newValue);
              }}
              getOptionLabel={(option: MessageGroupMember) =>
                `${option.contactInfo.firstName} ${option.contactInfo.lastName}` || ''
              }
              options={messageGroupUsers?.elements || []}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.userId}>
                    {`${option.contactInfo.firstName} ${option.contactInfo.lastName}` || ''}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  placeholder={t('FormFields.search.label').toString()}
                  {...params}
                  label=''
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ overflowY: 'auto', width: '100%' }}>
        {messages?.elements.map((message) => {
          const today = new Date();
          const date = new Date(message.createdAt);
          const isToday =
            date.getDate() === today.getDate() && date.getMonth() === today.getMonth();

          return (
            <MessageElement
              key={message.id}
              title={message.userFullName || message.groupName}
              subtitle={message.subject}
              description={message.content}
              senderName={message.senderName}
              date={
                isToday
                  ? t('MessagesView.chatbox.todayAt', {
                      time: formatTimeWithLocale(
                        dayjs(message.createdAt).tz(operatorTimezone),
                        i18n.language
                      ),
                    })
                  : formatDateTimeShortWithLocale(
                      dayjs(message.createdAt).tz(operatorTimezone),
                      i18n.language
                    )
              }
              seenText={getSeenText(message.seenCount, message.totalCount)}
            />
          );
        })}
      </Box>
    </Box>
  );
}

export default MessagesHistoryList;
